<script setup lang="ts">
import { onMounted, computed } from 'vue';
import { useForm } from 'vee-validate';
import { yup, citizenshipIsTaxIdCountry } from '@clearbanc/validation';
import { format } from 'date-fns';
import { personalTaxIdLabelShort, isPersonalTaxIdMasked } from '@/utils/local';
import { useVerificationCodes } from '@/composables/useVerificationCodes';
import InputTextGroup from '@/components/forms/groups/InputTextGroup.vue';
import CalendarGroup from '@/components/forms/groups/CalendarGroup.vue';
import { UserResponseDto } from '@clearbanc/baus-sdk';

interface UserInformationForm {
  firstName?: string;
  lastName?: string;
  personalTaxId?: string;
  passportNumber?: string;
  birthday?: string;
}

const props = defineProps<{ user: UserResponseDto }>();

// Use the verification codes composable
const {
  hasUserFirstNameIssue,
  hasUserLastNameIssue,
  hasUserDOBIssue,
  hasUserSSNIssue,
} = useVerificationCodes();

// Computed properties for cleaner template logic
const showFirstRow = computed(
  () => hasUserFirstNameIssue.value || hasUserLastNameIssue.value,
);
const showSecondRow = computed(
  () => hasUserSSNIssue.value || hasUserDOBIssue.value,
);
const showTaxIdField = computed(
  () =>
    hasUserSSNIssue.value &&
    props.user?.citizenship &&
    citizenshipIsTaxIdCountry(props.user.citizenship),
);
const showPassportField = computed(
  () =>
    hasUserSSNIssue.value &&
    !(
      props.user?.citizenship &&
      citizenshipIsTaxIdCountry(props.user?.citizenship)
    ),
);

// ----------
// Form Logic
// ----------

const validationSchema = computed(() =>
  yup.object({
    firstName: yup.userName(),
    lastName: yup.userName(),
    personalTaxId: yup.string().when([], {
      is: () => {
        const userCitizenship = props.user?.citizenship;
        return (
          hasUserSSNIssue.value &&
          userCitizenship &&
          citizenshipIsTaxIdCountry(userCitizenship)
        );
      },
      then: (schema) => yup.ssn(props.user?.citizenship),
      otherwise: (schema) => schema.notRequired(),
    }),
    passportNumber: yup.string().when([], {
      is: () => {
        const userCitizenship = props.user?.citizenship;
        return (
          hasUserSSNIssue.value &&
          !(userCitizenship && citizenshipIsTaxIdCountry(userCitizenship))
        );
      },
      then: (schema) => yup.passport(props.user?.citizenship),
      otherwise: (schema) => schema.notRequired(),
    }),
    birthday: yup.string().when([], {
      is: () => hasUserDOBIssue.value,
      then: (schema) => yup.birthday(),
      otherwise: (schema) => schema.notRequired(),
    }),
  }),
);

const { values, setValues, validate } = useForm<UserInformationForm>({
  validationSchema,
});

async function validateAndGetValues() {
  const { valid } = await validate();
  if (valid) {
    let formattedBirthday;
    if (values.birthday) {
      try {
        // Check if it's already in yyyy-MM-dd format
        if (/^\d{4}-\d{2}-\d{2}$/.test(values.birthday)) {
          formattedBirthday = values.birthday;
        } else {
          // Try to parse it as a Date object first, then format
          const birthdayDate = new Date(values.birthday);
          if (!isNaN(birthdayDate.getTime())) {
            formattedBirthday = format(birthdayDate, 'yyyy-MM-dd');
          }
        }
      } catch (_error) {
        // Fallback to original value if formatting fails
        formattedBirthday = values.birthday;
      }
    }

    return {
      firstName: values.firstName,
      lastName: values.lastName,
      personalTaxId: values.personalTaxId,
      passportNumber: values.passportNumber,
      birthday: formattedBirthday,
    };
  }
  return undefined;
}

defineExpose({
  validateAndGetValues,
});

function populateSavedValues() {
  const { firstName, lastName, personalTaxId, passportNumber, birthday } =
    props.user;

  setValues({
    ...(firstName && { firstName }),
    ...(lastName && { lastName }),
    ...(personalTaxId &&
      !isPersonalTaxIdMasked(personalTaxId) && { personalTaxId }),
    ...(passportNumber && { passportNumber }),
    ...(birthday && { birthday: birthday.toString().split('T')[0] }),
  });
}

// -----------------
// Lifecycle Methods
// -----------------

onMounted(() => {
  populateSavedValues();
});
</script>

<template>
  <div v-if="showFirstRow || showSecondRow" class="c-group">
    <div class="c-group__header">
      <h3 class="c-group__header__title">Owner Information</h3>
    </div>
    <div v-if="showFirstRow" class="c-form__row grid grid-cols-12">
      <div v-if="hasUserFirstNameIssue" class="col-span-6 md:col-span-12">
        <InputTextGroup name="firstName" label="First Name" />
      </div>
      <div v-if="hasUserLastNameIssue" class="col-span-6 md:col-span-12">
        <InputTextGroup name="lastName" label="Last Name" />
      </div>
    </div>
    <div v-if="showSecondRow" class="c-form__row grid grid-cols-12">
      <div v-if="hasUserSSNIssue" class="col-span-6 md:col-span-12">
        <InputTextGroup
          v-if="showTaxIdField"
          v-only-numbers
          name="personalTaxId"
          :label="
            personalTaxIdLabelShort(user?.citizenship) || 'Personal Tax ID'
          "
          :placeholder="
            isPersonalTaxIdMasked(user?.personalTaxId)
              ? user?.personalTaxId
              : undefined
          "
          :use-grouping="false"
        />
        <InputTextGroup
          v-else-if="showPassportField"
          name="passportNumber"
          label="Passport Number"
          :use-grouping="false"
        />
      </div>
      <div v-if="hasUserDOBIssue" class="col-span-6 md:col-span-12">
        <CalendarGroup
          label="Date of Birth"
          name="birthday"
          :max-date="new Date()"
          :manual-input="false"
          placeholder="yyyy-mm-dd"
          show-icon
        />
      </div>
    </div>
  </div>
</template>

<style module lang="less"></style>
